import {Dictionary, convertAllErrorsFromBeToFormError, putAll, takeLatestF, validateSchema} from '@fl/cmsch-fe-library';
import {isUndefined, keys, omitBy} from 'lodash/fp';
import {SagaIterator} from 'redux-saga';
import {isEmpty, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {ValidateAssociationBindingManager} from 'api/gen/ValidateAssociationBindingManager';
import {ValidateCreateAssociationBindingAdmin} from 'api/gen/ValidateCreateAssociationBindingAdmin';
import {t} from 'app/translations';
import {canRunAsyncValidation, formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {ValidateAsociationErrorsSchema} from '../../types/errors/validate-association-errors';
import {
    subjectManagementFormName,
    SubjectManagementFormTag,
    SubjectManagementFormValuesErrors,
} from '../../types/subject-management-form-values';
import {ValidateManagementAssociationAction} from '../action';

interface ApiTypes {
    associationId: number;
    roleIds: Array<number> | null;
    userProfileId: number;
    approvedFrom: string;
    approvedTo: string | null;
    tag: SubjectManagementFormTag;
    userProfileRoleIds: Array<number>;
}
const getApiValues = ({
    associationId,
    roleIds,
    userProfileId,
    approvedFrom,
    approvedTo,
    userProfileRoleIds,
    tag,
}: ApiTypes): ValidateAssociationBindingManager => tag === 'ValidateCreateAssociationBindingManager'
    ? ({
        tag,
        binding: {
            approvedFrom,
            approvedTo,
            associationId,
            roleIds: opt(roleIds).orCrash('No roleIds found'),
            userProfileId,
        },
    })
    : {
        tag,
        binding: {
            approvedTo,
            userProfileAssociationRoleId: opt(userProfileRoleIds).headIn().orCrash('no userProfileRoleIds found'),
        },
    };

const mapErrors = (
    errors: Dictionary<keyof ValidateCreateAssociationBindingAdmin, string>,
): SubjectManagementFormValuesErrors => {
    const validErrors = validateSchema(ValidateAsociationErrorsSchema, 'FE')(errors).binding;

    return omitBy(isUndefined)<SubjectManagementFormValuesErrors>({
        approvedFrom: validErrors.approvedFrom,
        approvedTo: validErrors.approvedTo,
        subjectId: validErrors.associationId,
        userProfileId: validErrors.userProfileId,
        roleIds: validErrors.roleIds,
    });
};

// eslint-disable-next-line max-lines-per-function
function* execute({payload: {source}}: ValidateManagementAssociationAction): SagaIterator {
    const {
        approvedFrom,
        approvedTo,
        subjectId,
        roleIds,
        userProfileId,
        tag,
        userProfileRoleIds,
    } = (
        yield* select(formHelpers.formValues(subjectManagementFormName))
    ).orCrash(`no ${subjectManagementFormName} form values`);

    const pristine = yield* select(formHelpers.isPristine(subjectManagementFormName));

    const syncErrors = yield* select(formHelpers.getSyncErrors(subjectManagementFormName));
    if (syncErrors.exists(errors => !canRunAsyncValidation(errors, source))) return;

    if (
        approvedFrom === null
        || subjectId === null
        || userProfileId === null
        || isEmpty(roleIds)
        || pristine
        || (approvedTo !== null && approvedFrom > approvedTo)
    ) return; // synchronously not valid

    yield* put(formHelpers.startAsyncValidation(subjectManagementFormName));

    const response = yield* call(
        Api.validateUserProfileAssociationRoleBySubjectManager,
        getApiValues({
            associationId: subjectId,
            roleIds,
            userProfileId,
            approvedFrom,
            approvedTo,
            tag: opt(tag).orCrash('Tag does not exist'),
            userProfileRoleIds: opt(userProfileRoleIds).orElse([]),
        }),
    );

    if (!response.isSuccess) {
        if (response.isBadRequest) {
            const errors = convertAllErrorsFromBeToFormError(response.data);
            const associationErrors = mapErrors(errors.errors);
            yield* put(formHelpers.stopAsyncValidation(subjectManagementFormName, associationErrors));
            const fieldsWithNewErrors = keys(associationErrors) as Array<keyof typeof associationErrors>;

            if (!isEmpty(fieldsWithNewErrors)) {
                yield* put(formHelpers.touch(subjectManagementFormName, ...fieldsWithNewErrors));
            }
        } else {
            yield putAll(showBeError(response, t('common')('validateEntry')));
        }
    } else {
        yield* put(formHelpers.stopAsyncValidation(subjectManagementFormName, {}));
    }
}

/** Runs the admin saga that validates association-management form values before save. */
export function* validateManagementAssociationSaga(): SagaIterator {
    yield takeLatestF('admin/VALIDATE_MANAGEMENT_ASSOCIATION', execute);
}
